import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Scissors, CheckCircle, XCircle, Loader2, Home } from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";

const MagicLoginPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [error, setError] = useState("");
  const { setToken, setUser } = useAuth();
  const navigate = useNavigate();
  
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("Invalid magic link - no token provided");
      return;
    }

    const verifyMagicLink = async () => {
      try {
        const response = await axios.post(`${API}/auth/magic-link/verify?token=${token}`);
        
        // Store the auth token
        localStorage.setItem("token", response.data.access_token);
        setToken(response.data.access_token);
        setUser(response.data.user);
        
        // Skip guide for magic link users
        localStorage.setItem("myhairchair_guide_skipped", "true");
        
        setStatus("success");
        
        // Redirect after 2 seconds
        setTimeout(() => {
          const role = response.data.user?.role;
          navigate(role === "stylist" ? "/stylist" : "/dashboard", { replace: true });
        }, 2000);
        
      } catch (err) {
        setStatus("error");
        setError(err.response?.data?.detail || "This magic link is invalid or has expired");
      }
    };

    verifyMagicLink();
  }, [token, navigate, setToken, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 px-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-red-500 to-amber-400 rounded-xl flex items-center justify-center shadow-lg">
            <Scissors className="w-6 h-6 text-white" />
          </div>
          <span className="font-display font-bold text-2xl text-gray-800">MyHairChair</span>
        </div>

        {/* Verifying State */}
        {status === "verifying" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-bold text-gray-800 mb-2">
              Signing you in...
            </h1>
            <p className="text-gray-500">
              Please wait while we verify your magic link
            </p>
          </div>
        )}

        {/* Success State */}
        {status === "success" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-display text-2xl font-bold text-gray-800 mb-2">
              You're in! 🎉
            </h1>
            <p className="text-gray-500">
              Redirecting you to your dashboard...
            </p>
          </div>
        )}

        {/* Error State */}
        {status === "error" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-display text-2xl font-bold text-gray-800 mb-2">
              Link Expired
            </h1>
            <p className="text-gray-500 mb-6">
              {error}
            </p>
            <div className="space-y-3">
              <Link 
                to="/login"
                className="block w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl shadow-lg hover:from-orange-600 hover:to-amber-600 transition-all"
              >
                Try Again
              </Link>
              <Link 
                to="/"
                className="flex items-center justify-center gap-2 text-gray-500 hover:text-orange-600 transition-colors"
                data-testid="back-to-app-btn"
              >
                <Home className="w-4 h-4" />
                Back to MyHairChair
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicLoginPage;
